import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Edit, Eye, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";

export default function MyProfile() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/profile/me").then(({ data }) => setMe(data)).catch(() => setMe({}));
  }, []);

  const completion = me?.completion?.score ?? 0;
  const status = me?.profile?.verification?.status || "not_verified";
  const isVerified = status === "verified";

  const missingLabels = {
    it: {
      display_name: "Aggiungi il tuo nome visualizzato",
      relationship_goals: "Indica cosa cerchi",
      interested_in_genders: "Indica chi ti interessa",
      bio: "Scrivi una breve bio",
      photos: "Aggiungi una foto",
      interests: "Seleziona qualche interesse",
      interests_more: "Aggiungi almeno 5 interessi",
      values: "Scegli i tuoi valori",
    },
    en: {
      display_name: "Add your display name",
      relationship_goals: "Say what you're looking for",
      interested_in_genders: "Choose who you're interested in",
      bio: "Write a short bio",
      photos: "Add a photo",
      interests: "Pick some interests",
      interests_more: "Add at least 5 interests",
      values: "Choose your values",
    },
  };

  return (
    <div className="max-w-3xl" data-testid="my-profile-page">
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>{t("app.nav.profile")}</p>
      <h1 className="mt-3 text-4xl md:text-5xl">{t("app.profile.title")}</h1>

      <div className="mt-10 nvl-card p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display text-3xl">{me?.profile?.display_name || user?.email}</div>
            <div className="mt-1 text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {user?.email} · {user?.language?.toUpperCase()} · {user?.role}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/app/profile/edit" className="nvl-btn nvl-btn-primary" data-testid="my-profile-edit">
              <Edit size={14} className="mr-2" /> {lang === "it" ? "Modifica" : "Edit"}
            </Link>
            <Link to="/app/profile/preview" className="nvl-btn nvl-btn-outline" data-testid="my-profile-preview">
              <Eye size={14} className="mr-2" /> {lang === "it" ? "Anteprima" : "Preview"}
            </Link>
          </div>
        </div>

        <div className="mt-8">
          <p className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {lang === "it" ? "Completezza profilo" : "Profile completion"} · <span data-testid="profile-completion-score">{completion}%</span>
          </p>
          <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "hsl(var(--nvl-border))" }}>
            <div className="h-full" style={{ width: `${completion}%`, backgroundColor: "hsl(var(--nvl-accent))", transition: "width 300ms" }} />
          </div>
        </div>

        {me?.completion?.missing?.length ? (
          <div className="mt-6">
            <p className="nvl-overline" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {lang === "it" ? "Migliora il tuo profilo" : "Improve your profile"}
            </p>
            <ul className="mt-3 space-y-1 text-sm">
              {me.completion.missing.slice(0, 4).map((k) => (
                <li key={k} className="flex items-center gap-2" data-testid={`missing-${k}`}>
                  <span style={{ color: "hsl(var(--nvl-accent))" }}>•</span>
                  {missingLabels[lang][k] || k}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
            style={{
              backgroundColor: isVerified ? "hsl(var(--nvl-accent))" : "hsl(var(--muted))",
              color: isVerified ? "hsl(var(--nvl-bg))" : "hsl(var(--nvl-ink))",
            }}
            data-testid="verification-badge"
          >
            <ShieldCheck size={14} />
            {isVerified
              ? lang === "it" ? "Identità verificata" : "Identity verified"
              : lang === "it" ? "Non verificato" : "Not verified"}
          </span>
          <span className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {lang === "it" ? "Iscrizione: " : "Membership: "}{user?.membership_status}
          </span>
        </div>
      </div>
    </div>
  );
}
